import { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useSettings } from "@/context/SettingsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Dumbbell } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Login = () => {
  const navigate = useNavigate();
  const { refreshSettings } = useSettings();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    company_id: ""
  });
  const [loading, setLoading] = useState(false);
  const [tenants, setCompanys] = useState([]);
  const [showCompanySelect, setShowCompanySelect] = useState(false);
  const [userData, setUserData] = useState(null);

  const handleInitialLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/login`, {
        email: formData.email,
        password: formData.password
      });
      
      if (response.data.tenants) {
        // Multiple tenants - show selection
        setCompanys(response.data.tenants);
        setUserData(response.data.user);
        setShowCompanySelect(true);
      } else {
        // Single tenant or tenant selected - proceed
        localStorage.setItem("token", response.data.access_token);
        localStorage.setItem("user", JSON.stringify(response.data.user));
        toast.success("Login successful!");
        navigate("/");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleCompanyLogin = async () => {
    if (!formData.company_id) {
      toast.error("Please select a company");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/login`, {
        email: formData.email,
        password: formData.password,
        company_id: formData.company_id
      });
      
      localStorage.setItem("token", response.data.access_token);
      localStorage.setItem("user", JSON.stringify(response.data.user));
      await refreshSettings();
      toast.success("Login successful!");
      navigate("/");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  if (showCompanySelect) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-zinc-50 to-zinc-100">
        <div className="w-full max-w-md p-8 bg-white rounded-2xl shadow-xl border border-zinc-200">
          <div className="flex flex-col items-center mb-8">
            <div className="w-16 h-16 bg-lime-400 rounded-full flex items-center justify-center mb-4">
              <Dumbbell size={32} className="text-zinc-900" />
            </div>
            <h1 className="text-3xl font-black tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Select Company
            </h1>
            <p className="text-zinc-500 text-sm mt-2">Welcome back, {userData?.full_name}</p>
          </div>

          <div className="space-y-6">
            <div>
              <Label htmlFor="tenant">Choose your company</Label>
              <Select
                value={formData.company_id}
                onValueChange={(value) => setFormData({ ...formData, company_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a company" />
                </SelectTrigger>
                <SelectContent>
                  {tenants.map((tenant) => (
                    <SelectItem key={tenant.id} value={tenant.id}>
                      {tenant.name} ({tenant.business_type})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button
              onClick={handleCompanyLogin}
              className="w-full bg-lime-400 text-zinc-900 hover:bg-lime-500 font-bold"
              disabled={loading || !formData.company_id}
            >
              {loading ? "Logging in..." : "Continue"}
            </Button>

            <Button
              variant="outline"
              onClick={() => {
                setShowCompanySelect(false);
                setCompanys([]);
                setUserData(null);
                setFormData({ email: "", password: "", company_id: "" });
              }}
              className="w-full"
            >
              Back to Login
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-zinc-50 to-zinc-100">
      <div className="w-full max-w-md p-8 bg-white rounded-2xl shadow-xl border border-zinc-200">
        <div className="flex flex-col items-center mb-8">
          <div className="w-16 h-16 bg-lime-400 rounded-full flex items-center justify-center mb-4">
            <Dumbbell size={32} className="text-zinc-900" />
          </div>
          <h1 className="text-3xl font-black tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Welcome Back
          </h1>
          <p className="text-zinc-500 text-sm mt-2">Login to your IronPulse account</p>
        </div>

        <form onSubmit={handleInitialLogin} className="space-y-6">
          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="your.email@example.com"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
            />
          </div>

          <div>
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              placeholder="••••••••"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required
            />
          </div>

          <Button
            type="submit"
            className="w-full bg-lime-400 text-zinc-900 hover:bg-lime-500 font-bold"
            disabled={loading}
          >
            {loading ? "Logging in..." : "Login"}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default Login;